import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import { ScheduleDay } from "api/Dashboard";
import { Panel, compactCurrency } from "./shared";

/**
 * The next seven days - work booked and obligations falling due on one
 * rail (spec 3.4).
 *
 * A RAIL, NOT A MONTH GRID. The dashboard answers "what is coming";
 * anything more than that is the calendar's job, and a month grid here
 * would make the dashboard the place everything accumulates again.
 *
 * Quiet days are drawn, not skipped. A rail that only renders days with
 * something on them compresses the week and makes Friday look like
 * tomorrow.
 */
const ScheduleRail: React.FC<{ schedule: ScheduleDay[] }> = ({ schedule }) => {
  const navigate = useNavigate();

  const open = (item: { entityType: string; entityId: string }) => {
    if (item.entityType === "invoice") navigate("/invoices/" + item.entityId);
    if (item.entityType === "project") navigate("/projects/" + item.entityId);
  };

  return (
    <Panel
      title="Coming up"
      subtitle="Next seven days"
      right={
        <button
          type="button"
          onClick={() => navigate("/calendar")}
          style={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            fontWeight: 500,
            padding: "5px 12px",
            borderRadius: "6px",
            border: "1px solid " + themevals.colors.inputOutline,
            backgroundColor: "#FFFFFF",
            color: themevals.colors.base_800,
            cursor: "pointer",
          }}
        >
          Full calendar
        </button>
      }
      sx={{ marginBottom: "24px" }}
    >
      <Flex sx={{ gap: "8px", overflowX: "auto" }}>
        {schedule.map((day) => (
          <Box
            key={day.date}
            sx={{
              flex: "1 1 0",
              minWidth: "150px",
              border: "1px solid",
              borderColor: day.isToday
                ? themevals.colors.base_800
                : themevals.colors.inputOutline,
              borderRadius: "6px",
              padding: "10px",
              backgroundColor: day.isToday ? "#F7F9FC" : "#FFFFFF",
            }}
          >
            <Flex
              sx={{
                alignItems: "baseline",
                gap: "6px",
                marginBottom: "8px",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: themevals.colors.dkgrey,
                }}
              >
                {day.weekday}
              </Box>
              <Box sx={{ fontSize: "15px", fontWeight: 600, color: "base_800" }}>
                {day.dayOfMonth}
              </Box>
            </Flex>

            {day.items.length === 0 ? (
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  color: themevals.colors.dkgrey,
                }}
              >
                Nothing booked
              </Box>
            ) : (
              day.items.map((item) => (
                <Box
                  key={item.entityType + item.entityId}
                  onClick={() => open(item)}
                  sx={{
                    cursor: "pointer",
                    marginBottom: "8px",
                    paddingLeft: "8px",
                    borderLeft: "3px solid",
                    // Work and money are both "what is coming", but they
                    // are not the same kind of thing, so the rail marks
                    // which is which rather than blending them.
                    borderColor:
                      item.type === "invoice_due"
                        ? themevals.colors.warning
                        : themevals.colors.info,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "base_800",
                    }}
                  >
                    {item.title}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "11px",
                      color: themevals.colors.dkgrey,
                    }}
                  >
                    {item.subtitle}
                    {item.amount !== null
                      ? " - " + compactCurrency(item.amount)
                      : ""}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        ))}
      </Flex>
    </Panel>
  );
};

export default ScheduleRail;
